import { z } from "zod";
import { PASSWORD_MIN_LENGTH } from "../constants/index.ts";
import { bilingualStringSchema } from "./bilingual.ts";
import { businessTypeSchema, facilityTypeSchema } from "./clinic.ts";

const passwordSchema = z
	.string()
	.min(
		PASSWORD_MIN_LENGTH,
		`Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
	)
	.max(128)
	.refine((v) => /[A-Z]/.test(v), "Must contain at least one uppercase letter")
	.refine((v) => /[a-z]/.test(v), "Must contain at least one lowercase letter")
	.refine((v) => /[0-9]/.test(v), "Must contain at least one digit");

const phoneSchema = z
	.string()
	.min(10)
	.max(20)
	.regex(/^\+?[0-9\s\-()]+$/, "Invalid phone number");

const slugSchema = z
	.string()
	.min(2)
	.max(50)
	.regex(
		/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
		"Invalid slug (lowercase letters, digits, hyphens)",
	);

// ===== Clinic signup (new tenant) =====
export const clinicSignupSchema = z.object({
	clinic: z.object({
		name: bilingualStringSchema,
		slug: slugSchema,
		// What kind of facility is signing up. Defaults to 'clinic' so the
		// existing clinic signup form keeps working without sending this field.
		facility_type: facilityTypeSchema.default("clinic"),
		business_type: businessTypeSchema,
		timezone: z.string().default("Asia/Baghdad"),
		default_language: z.enum(["ar", "en"]).default("ar"),
	}),
	owner: z.object({
		full_name: z.string().min(2).max(100),
		email: z.string().email(),
		phone: phoneSchema.optional(),
		password: passwordSchema,
		preferred_language: z.enum(["ar", "en"]).default("ar"),
	}),
	consent: z.object({
		terms: z.literal(true, {
			errorMap: () => ({ message: "Must accept terms" }),
		}),
		privacy: z.literal(true, {
			errorMap: () => ({ message: "Must accept privacy policy" }),
		}),
	}),
	// Cloudflare Turnstile token (G6). Optional in the schema — it's only
	// required+verified server-side when captcha is configured (dormant otherwise).
	captcha_token: z.string().optional(),
});
export type ClinicSignupInput = z.infer<typeof clinicSignupSchema>;

// ===== Login (email or phone) =====
export const loginSchema = z
	.object({
		email: z.string().email().optional(),
		phone: phoneSchema.optional(),
		password: z.string().min(1),
	})
	.refine((d) => d.email || d.phone, "Email or phone is required");
export type LoginInput = z.infer<typeof loginSchema>;

// ===== Refresh =====
export const refreshSchema = z.object({
	refreshToken: z.string().min(20),
});
export type RefreshInput = z.infer<typeof refreshSchema>;

// ===== Email verification (G5) =====
export const verifyEmailSchema = z.object({
	token: z.string().min(10).max(200),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendVerificationSchema = z.object({
	email: z.string().email(),
});
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

// ===== Change own password (authenticated) =====
export const changePasswordSchema = z.object({
	current_password: z.string().min(1),
	new_password: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ===== Owner resets a member's password =====
export const resetMemberPasswordSchema = z.object({
	new_password: passwordSchema,
});
export type ResetMemberPasswordInput = z.infer<
	typeof resetMemberPasswordSchema
>;

// ===== JWT payload (access token) =====
export const accessTokenPayloadSchema = z.object({
	sub: z.string().uuid(), // user_id
	clinic_id: z.string().uuid(),
	role: z.string(),
	// standard JWT claims
	iat: z.number(),
	exp: z.number(),
});
export type AccessTokenPayload = z.infer<typeof accessTokenPayloadSchema>;

// ===== Auth response =====
export interface AuthResponse {
	user: {
		id: string;
		email: string | null;
		phone: string | null;
		full_name: string;
		preferred_language: "ar" | "en";
	};
	clinic: {
		id: string;
		slug: string;
		name: { ar: string; en?: string };
		/** clinic | lab | pharmacy | radiology — drives which dashboard/module the staff see. */
		facility_type: "clinic" | "lab" | "pharmacy" | "radiology";
		/**
		 * Clinical specialty (cosmetic, dental, …). Drives specialty-specific
		 * UI like the dental odontogram. 'general' for labs/pharmacies.
		 */
		business_type: string;
		role: string;
		/** Effective permission set for this member. Owner = all. */
		permissions: string[];
		/** Per-facility feature flags (default-enabled; admin can disable). */
		feature_flags: Record<string, boolean>;
		/** Inpatient/HIS capability (opt-in, super-admin controlled; default off). */
		inpatient_enabled?: boolean;
	};
	tokens: {
		access_token: string;
		refresh_token: string;
		expires_in: number;
	};
}

/**
 * Self-signup result (G5). When email verification is dormant the owner is
 * logged straight in (full AuthResponse). When an email provider is configured,
 * signup instead returns a verification prompt and issues NO tokens — the owner
 * must click the emailed link first. Discriminated by `verification_required`.
 */
export interface VerificationRequiredResult {
	verification_required: true;
	email: string;
}
export type SignupResult = AuthResponse | VerificationRequiredResult;
